import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Patient {
  id: number;
  medical_record_number: string;
  name: string;
  date_of_birth: string;
  gender: 'male' | 'female';
  age: number;
  phone: string;
  email?: string;
  address: string;
  occupation?: string;
  insurance_type?: string;
  insurance_number?: string;
  allergies?: string;
  medical_history?: string;
  is_active: boolean;
  visit_count: number;
  latest_visit?: string;
}

export interface PatientVisit {
  id: number;
  visit_number: string;
  patient_id: number;
  visit_date: string;
  visit_time: string;
  service_type: string;
  specialty?: string;
  service_method: string;
  payment_method: string;
  status: 'registered' | 'waiting' | 'in_progress' | 'completed' | 'cancelled';
  total_amount: number;
  discount_amount: number;
  final_amount: number;
  notes?: string;
  patient_name?: string;
  company_name?: string;
}

export interface PatientSearchResult {
  id: number;
  medical_record_number: string;
  name: string;
  age: number;
  phone: string;
  insurance_type?: string;
  latest_visit?: string;
  visit_count: number;
}

interface PatientState {
  patients: Patient[];
  currentPatient: Patient | null;
  selectedPatient: Patient | null;
  patientVisits: PatientVisit[];
  searchResults: PatientSearchResult[];
  isLoading: boolean;
  isSearching: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    perPage: number;
  };
  filters: {
    search: string;
    insurance_type: string;
    service_type: string;
    date_from: string;
    date_to: string;
  };
}

const initialState: PatientState = {
  patients: [],
  currentPatient: null,
  selectedPatient: null,
  patientVisits: [],
  searchResults: [],
  isLoading: false,
  isSearching: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    perPage: 15,
  },
  filters: {
    search: '',
    insurance_type: '',
    service_type: '',
    date_from: '',
    date_to: '',
  },
};

const patientSlice = createSlice({
  name: 'patients',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSearching: (state, action: PayloadAction<boolean>) => {
      state.isSearching = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Patient list management
    setPatientsSuccess: (state, action: PayloadAction<{
      data: Patient[];
      pagination: {
        current_page: number;
        last_page: number;
        total: number;
        per_page: number;
      };
    }>) => {
      state.patients = action.payload.data;
      state.pagination = {
        currentPage: action.payload.pagination.current_page,
        totalPages: action.payload.pagination.last_page,
        totalItems: action.payload.pagination.total,
        perPage: action.payload.pagination.per_page,
      };
      state.isLoading = false;
      state.error = null;
    },

    // Patient search
    setSearchResults: (state, action: PayloadAction<PatientSearchResult[]>) => {
      state.searchResults = action.payload;
      state.isSearching = false;
    },

    // Current patient management
    setCurrentPatient: (state, action: PayloadAction<Patient | null>) => {
      state.currentPatient = action.payload;
    },
    setSelectedPatient: (state, action: PayloadAction<Patient | null>) => {
      state.selectedPatient = action.payload;
    },

    // Patient CRUD operations
    addPatient: (state, action: PayloadAction<Patient>) => {
      state.patients.unshift(action.payload);
      state.pagination.totalItems += 1;
    },
    updatePatient: (state, action: PayloadAction<Patient>) => {
      const index = state.patients.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.patients[index] = action.payload;
      }
      if (state.currentPatient?.id === action.payload.id) {
        state.currentPatient = action.payload;
      }
    },
    removePatient: (state, action: PayloadAction<number>) => {
      state.patients = state.patients.filter(p => p.id !== action.payload);
      state.pagination.totalItems -= 1;
      if (state.currentPatient?.id === action.payload) {
        state.currentPatient = null;
      }
    },

    // Patient visits
    setPatientVisits: (state, action: PayloadAction<PatientVisit[]>) => {
      state.patientVisits = action.payload;
    },
    addPatientVisit: (state, action: PayloadAction<PatientVisit>) => {
      state.patientVisits.unshift(action.payload);
    },
    updatePatientVisit: (state, action: PayloadAction<PatientVisit>) => {
      const index = state.patientVisits.findIndex(v => v.id === action.payload.id);
      if (index !== -1) {
        state.patientVisits[index] = action.payload;
      }
    },

    // Filters and pagination
    setFilters: (state, action: PayloadAction<Partial<typeof initialState.filters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.currentPage = 1;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },

    // Clear data
    clearPatients: (state) => {
      state.patients = [];
      state.pagination = initialState.pagination;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    clearCurrentPatient: (state) => {
      state.currentPatient = null;
      state.patientVisits = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setLoading,
  setSearching,
  setError,
  setPatientsSuccess,
  setSearchResults,
  setCurrentPatient,
  setSelectedPatient,
  addPatient,
  updatePatient,
  removePatient,
  setPatientVisits,
  addPatientVisit,
  updatePatientVisit,
  setFilters,
  clearFilters,
  setCurrentPage,
  clearPatients,
  clearSearchResults,
  clearCurrentPatient,
  clearError,
} = patientSlice.actions;

// Selectors
export const selectPatients = (state: { patients: PatientState }) => state.patients.patients;
export const selectCurrentPatient = (state: { patients: PatientState }) => state.patients.currentPatient;
export const selectSelectedPatient = (state: { patients: PatientState }) => state.patients.selectedPatient;
export const selectPatientVisits = (state: { patients: PatientState }) => state.patients.patientVisits;
export const selectSearchResults = (state: { patients: PatientState }) => state.patients.searchResults;
export const selectPatientsLoading = (state: { patients: PatientState }) => state.patients.isLoading;
export const selectPatientsSearching = (state: { patients: PatientState }) => state.patients.isSearching;
export const selectPatientsError = (state: { patients: PatientState }) => state.patients.error;
export const selectPatientsPagination = (state: { patients: PatientState }) => state.patients.pagination;
export const selectPatientsFilters = (state: { patients: PatientState }) => state.patients.filters;

export default patientSlice.reducer;